import type { ServerConnection } from "@/context/server"
import type { SummaryScope, WorkspaceSummary } from "@/pages/project-hub/project-hub-summary"
import { jsonArray, jsonNumber, jsonObject, jsonString } from "@/transport/decode"
import { expectJson, expectResponse, serverFetch } from "@/transport/http"
import { sessionDecoder } from "@/transport/session-decode"

export type OracleSummaryResponse = {
  summary: WorkspaceSummary
  scope: SummaryScope
  generatedAt: number
  degraded?: boolean
  failedBatches?: number
  fallbackStage?: string
}

function decodeWorkspaceSummary(value: unknown): WorkspaceSummary {
  const summary = jsonObject(value, "Oracle returned an invalid summary")
  jsonString(summary.overview, "Oracle returned an invalid summary")
  for (const key of ["blocked", "decisions"] as const) {
    for (const value of jsonArray(summary[key], "Oracle returned an invalid summary")) {
      const item = jsonObject(value, "Oracle returned an invalid summary")
      jsonString(item.text, "Oracle returned an invalid summary")
      jsonArray(item.sessionIDs, "Oracle returned an invalid summary").forEach((id) => jsonString(id, "Oracle returned an invalid summary"))
    }
  }
  if (summary.sources !== undefined) jsonObject(summary.sources, "Oracle returned an invalid summary")
  return summary as WorkspaceSummary
}

function decodeOracleSummary(value: unknown): OracleSummaryResponse {
  const result = jsonObject(value, "Oracle returned an invalid summary response")
  if (!["since_last_summary", "24h", "7d", "all"].includes(String(result.scope))) {
    throw new Error("Oracle returned an invalid summary response")
  }
  jsonNumber(result.generatedAt, "Oracle returned an invalid summary response")
  if (result.degraded !== undefined && typeof result.degraded !== "boolean") throw new Error("Oracle returned an invalid summary response")
  if (result.failedBatches !== undefined) jsonNumber(result.failedBatches, "Oracle returned an invalid summary response")
  if (result.fallbackStage !== undefined) jsonString(result.fallbackStage, "Oracle returned an invalid summary response")
  return { ...result, summary: decodeWorkspaceSummary(result.summary) } as OracleSummaryResponse
}

const decodeOracleSession = sessionDecoder("Oracle returned an invalid session")

export async function fetchOracleSession(input: {
  server: ServerConnection.HttpBase
  workspaceID: string
  fetch?: typeof globalThis.fetch
  signal?: AbortSignal
}) {
  return expectJson(
    await serverFetch(
      input.server,
      `/project/${encodeURIComponent(input.workspaceID)}/oracle/session`,
      { signal: input.signal },
      { fetch: input.fetch },
    ),
    decodeOracleSession,
    { error: "Oracle session request failed" },
  )
}

/** Queues the exhaustive cleanup as a turn in the workspace Oracle chat. */
export async function organizeOracleSessions(input: {
  server: ServerConnection.HttpBase
  workspaceID: string
  signal?: AbortSignal
}) {
  await expectResponse(
    await serverFetch(input.server, `/project/${encodeURIComponent(input.workspaceID)}/oracle/organize`, {
      method: "POST",
      signal: input.signal,
    }),
    { error: "Auto-organize failed" },
  )
}

/** Moves the hidden Oracle chat into a visible chat that stays an Oracle. */
export async function promoteOracleSession(input: {
  server: ServerConnection.HttpBase
  workspaceID: string
  sessionID: string
  signal?: AbortSignal
}) {
  return expectJson(
    await serverFetch(
      input.server,
      `/project/${encodeURIComponent(input.workspaceID)}/oracle/session/${encodeURIComponent(input.sessionID)}/promote`,
      { method: "POST", signal: input.signal },
    ),
    (value) => {
      const result = jsonObject(value, "Oracle returned an invalid promotion")
      const { oracle, ...session } = result
      return {
        session: decodeOracleSession(session),
        oracle: oracle === undefined ? undefined : decodeOracleSession(oracle),
      }
    },
    { error: "Could not move the Oracle chat" },
  )
}

export async function requestOracleSummary(input: {
  server: ServerConnection.HttpBase
  projectID: string
  method: "GET" | "POST"
  body?: unknown
  signal?: AbortSignal
}) {
  const response = await serverFetch(input.server, `/project/${encodeURIComponent(input.projectID)}/oracle/summary`, {
    method: input.method,
    headers: input.method === "POST" ? { "Content-Type": "application/json" } : undefined,
    body: input.method === "POST" ? JSON.stringify(input.body) : undefined,
    signal: input.signal,
  })
  if (response.status === 204 || (input.method === "GET" && response.status === 404)) return undefined
  return expectJson(response, decodeOracleSummary)
}

/** A workspace's own Workspace Oracle prompt, null when it follows the account's, and the account's prompt it replaces. */
export type WorkspaceOraclePrompt = { text: string | null; inherited: string }

function decodeWorkspaceOraclePrompt(value: unknown): WorkspaceOraclePrompt {
  const prompt = jsonObject(value, "Server returned an invalid Oracle prompt")
  return {
    text: prompt.text === null ? null : jsonString(prompt.text, "Server returned an invalid Oracle prompt"),
    inherited: jsonString(prompt.inherited, "Server returned an invalid Oracle prompt"),
  }
}

export async function fetchWorkspaceOraclePrompt(input: { server: ServerConnection.HttpBase; workspaceID: string }) {
  return expectJson(
    await serverFetch(input.server, `/project/${encodeURIComponent(input.workspaceID)}/oracle/prompt`),
    decodeWorkspaceOraclePrompt,
    { error: "Could not load the Oracle prompt" },
  )
}

/** Saves a workspace's own Oracle prompt; null follows the account's again. */
export async function saveWorkspaceOraclePrompt(input: { server: ServerConnection.HttpBase; workspaceID: string; text: string | null }) {
  return expectJson(
    await serverFetch(input.server, `/project/${encodeURIComponent(input.workspaceID)}/oracle/prompt`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: input.text }),
    }),
    decodeWorkspaceOraclePrompt,
    { error: "Could not save the Oracle prompt" },
  )
}

export async function resetOracleSession(input: {
  server: ServerConnection.HttpBase
  workspaceID: string
  sessionID: string
  signal?: AbortSignal
}) {
  return expectJson(
    await serverFetch(
      input.server,
      `/project/${encodeURIComponent(input.workspaceID)}/oracle/session/${encodeURIComponent(input.sessionID)}`,
      { method: "DELETE", signal: input.signal },
    ),
    decodeOracleSession,
    { error: "Oracle reset failed" },
  )
}
